/**
 * Utility to sync authentication state between sessionStorage and localStorage
 * This enables cross-tab authentication and logout synchronization
 */

// Keys that should be synced to localStorage for cross-tab access
const AUTH_KEYS = [
    'IdToken',
    'RefreshToken',
    'AccessToken',
    'email',
    'user_id',
    'id',
    'user_type',
    'session_token',
    'first_name',
    'last_name',
    'currentTimestamp'
];

// Key for logout event in localStorage
const LOGOUT_EVENT_KEY = 'arcm_logout_event';
const AUTH_SYNC_KEY = 'arcm_auth_synced';

/**
 * Sync authentication tokens from sessionStorage to localStorage
 */
export const syncAuthToLocalStorage = () => {
    try {
        const authData = {};
        let hasAuthData = false;

        AUTH_KEYS.forEach(key => {
            const value = sessionStorage.getItem(key);
            if (value) {
                authData[key] = value;
                localStorage.setItem(key, value);
                hasAuthData = true;
            }
        });

        if (hasAuthData) {
            localStorage.setItem(AUTH_SYNC_KEY, 'true');
        }
    } catch (error) {
        console.error('Error syncing auth to localStorage:', error);
    }
};

/**
 * Restore authentication tokens from localStorage to sessionStorage
 * This is called when a new tab opens
 */
export const restoreAuthFromLocalStorage = () => {
    try {
        const isSynced = localStorage.getItem(AUTH_SYNC_KEY);
        if (!isSynced) {
            return false;
        }

        let hasAuthData = false;
        AUTH_KEYS.forEach(key => {
            const value = localStorage.getItem(key);
            if (value) {
                sessionStorage.setItem(key, value);
                hasAuthData = true;
            }
        });

        return hasAuthData;
    } catch (error) {
        console.error('Error restoring auth from localStorage:', error);
        return false;
    }
};

/**
 * Clear authentication data from both sessionStorage and localStorage
 */
export const clearAuthData = () => {
    try {
        // Clear from sessionStorage
        AUTH_KEYS.forEach(key => {
            sessionStorage.removeItem(key);
        });

        // Clear from localStorage (except browser trust token)
        const browserTrustToken = localStorage.getItem('arcm_trust_browser_token');
        const logoutFromAllTabs = localStorage.getItem('LogoutfromAllTabs');

        AUTH_KEYS.forEach(key => {
            localStorage.removeItem(key);
        });
        localStorage.removeItem(AUTH_SYNC_KEY);

        // Restore browser trust token if it exists
        if (browserTrustToken) {
            localStorage.setItem('arcm_trust_browser_token', browserTrustToken);
        }
        if (logoutFromAllTabs) {
            localStorage.setItem('LogoutfromAllTabs', logoutFromAllTabs);
        }
    } catch (error) {
        console.error('Error clearing auth data:', error);
    }
};

/**
 * Broadcast logout event to all tabs
 */
export const broadcastLogout = () => {
    try {
        const logoutEvent = {
            timestamp: Date.now(),
            type: 'logout'
        };
        localStorage.setItem(LOGOUT_EVENT_KEY, JSON.stringify(logoutEvent));
        // Remove the event after a short delay to allow other tabs to detect it
        setTimeout(() => {
            localStorage.removeItem(LOGOUT_EVENT_KEY);
        }, 1000);
    } catch (error) {
        console.error('Error broadcasting logout:', error);
    }
};

/**
 * Set up listener for cross-tab logout events
 * @param {Function} onLogout - Callback function to execute when logout is detected
 * @returns {Function} Cleanup function to remove the listener
 */
export const setupLogoutListener = (onLogout) => {
    const handleStorageChange = (e) => {
        if (e.key === LOGOUT_EVENT_KEY && e.newValue) {
            try {
                const logoutEvent = JSON.parse(e.newValue);
                if (logoutEvent.type === 'logout') {
                    onLogout();
                }
            } catch (error) {
                console.error('Error parsing logout event:', error);
            }
        }
    };

    // Listen for storage events (cross-tab communication)
    // Storage events ONLY fire in OTHER tabs, not the tab that made the change
    // This ensures handleCrossTabLogout only runs in non-initiating tabs
    window.addEventListener('storage', handleStorageChange);

    // Return cleanup function
    return () => {
        window.removeEventListener('storage', handleStorageChange);
    };
};


